import Icon from '../../../Icon/Icon';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../containers/UseAuth';
import { useForecast } from '../../../../contexts/ForecastContext';
import ScreenDiagnostics from '../ScreenDiagnostics/ScreenDiagnostics';
import TableComp from './TableComp';
import canViewForecastDetails from './canViewForecastDetails';
import {
    formatDecimal as sharedFormatDecimal,
    formatWhole as sharedFormatWhole,
    formatSingleDecimal as sharedFormatSingleDecimal,
    formatPercent as sharedFormatPercent,
    formatCoveragePercent as sharedFormatCoveragePercent,
    formatRatio as sharedFormatRatio,
    formatFrequency as sharedFormatFrequency,
    formatCurrencyValue as sharedFormatCurrencyValue,
} from '../../../../utils/formatForecastNumber';

export const resolveImpressionsPerScreenHour = (
    averageImpressions,
    totalScreens
) => {
    if (
        averageImpressions === null ||
        averageImpressions === undefined ||
        averageImpressions === ''
    ) {
        return { value: null, tone: 'neutral' };
    }

    const normalizedAverage = Number(averageImpressions);
    const normalizedScreens = Number(totalScreens);

    if (
        !Number.isFinite(normalizedAverage) ||
        !Number.isFinite(normalizedScreens) ||
        normalizedScreens <= 0
    ) {
        return { value: null, tone: 'neutral' };
    }

    const value = Math.floor(normalizedAverage / normalizedScreens);
    return {
        value,
        tone: value <= 0 ? 'critical' : value <= 2 ? 'warning' : 'ok',
    };
};

const ForecastMetrics = ({
    setSelectedScreensLocal,
    selectedScreensLocal = [],
    tierList = {},
    campaign,
    basics,
    onClearScreensWithoutProvider,
    onUpdateSelectedScreens,
    calculatingForecast = false,
}) => {
    const { t } = useTranslation();
    const { forecast: metrics = {} } = useForecast();
    const metricsImpressions = metrics?.impressionsPerScreen;

    const rawBudget = basics?.budget?.budget ?? campaign?.budget?.budget;

    const userBudgetInput = Number(rawBudget ?? 0);
    const spentBudget = Number(metrics?.spentBudget ?? 0);

    const shouldShowBudgetWarning =
        spentBudget > 0 &&
        userBudgetInput > 0 &&
        spentBudget <= userBudgetInput * 0.2;

    const selectedCurrency =
        campaign?.selectedCurrency ?? basics?.selectedCurrency;
    const currencySymbol =
        selectedCurrency?.symbol !== undefined ? selectedCurrency.symbol : '';

    const auth = useAuth();
    const canViewDetails = canViewForecastDetails(auth);

    const [nonBuyingScreensExpanded, setNonBuyingScreensExpanded] =
        useState(false);
    const [impressionsPerScreenExpanded, setImpressionsPerScreenExpanded] =
        useState(false);

    const renderNonBuyingScreensSummary = () => {
        if (!screenCountWarnings || screenCountWarnings.length === 0)
            return null;

        return (
            <div className="mt-2">
                <div
                    id="non-buying-screens-details"
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        nonBuyingScreensExpanded
                            ? 'max-h-96 opacity-100'
                            : 'max-h-0 opacity-0'
                    }`}>
                    {t(
                        'sections.campaigns.new-campaign.wizard-audiences.forecast.no-impressions-bought'
                    )}
                    {' :'}
                    {nonBuyingScreensExpanded && (
                        <ul className="mt-2 pl-2 border-l-2 border-gray-200 space-y-1 max-h-80 overflow-y-auto">
                            {screenCountWarnings.map((screen) => (
                                <li
                                    key={screen.screen_id}
                                    className="text-xs text-gray-500">
                                    <span className="font-medium text-gray-700">
                                        {screen.screen_external_id}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        );
    };

    const currencyCode = selectedCurrency?.short_name?.toUpperCase() || 'USD';
    const formatMetricValue = (value) => {
        return sharedFormatDecimal(value);
    };
    const formatSingleDecimal = (value) => {
        return sharedFormatSingleDecimal(value);
    };
    const formatWholeNumber = (value) => {
        return sharedFormatWhole(value);
    };
    const formatExpositionTime = (value) => {
        if (
            value === null ||
            value === undefined ||
            Number.isNaN(Number(value))
        ) {
            return '-';
        }
        const totalSeconds = Math.max(0, Math.floor(Number(value)));
        const segments = [
            { label: 'd', value: Math.floor(totalSeconds / 86400) },
            {
                label: 'h',
                value: Math.floor((totalSeconds % 86400) / 3600),
            },
            { label: 'm', value: Math.floor((totalSeconds % 3600) / 60) },
            { label: 's', value: totalSeconds % 60 },
        ];
        const displayedSegments = segments.filter(({ value }) => value > 0);
        if (displayedSegments.length === 0) {
            return '0s';
        }
        return displayedSegments
            .map(({ value, label }) => `${value}${label}`)
            .join(' ');
    };
    const formatCoveragePercent = (value) => {
        return sharedFormatCoveragePercent(value);
    };

    const screensUsedCount = Array.isArray(metrics.screensUsed)
        ? metrics.screensUsed.length
        : 0;
    const screensNotUsedCount = Array.isArray(metrics.screensNotUsed)
        ? metrics.screensNotUsed.length
        : 0;
    const notMatchingScreensCount = Array.isArray(metrics.notMatchingScreens)
        ? metrics.notMatchingScreens.length
        : 0;
    const parsedScreensQuantity =
        metrics.screensQuantity !== undefined &&
        metrics.screensQuantity !== null
            ? Number(metrics.screensQuantity)
            : NaN;
    const totalScreensCount =
        !Number.isNaN(parsedScreensQuantity) && parsedScreensQuantity > 0
            ? parsedScreensQuantity
            : screensUsedCount + screensNotUsedCount;
    const displayedScreensUsed =
        screensUsedCount > 0
            ? screensUsedCount
            : totalScreensCount > 0
              ? Math.max(totalScreensCount - screensNotUsedCount, 0)
              : 0;
    const screenCountWarnings = Array.isArray(metrics.screenCountWarnings)
        ? metrics.screenCountWarnings
        : [];
    const warningScreensCount = screenCountWarnings.length;
    const hasScreenCountWarnings = warningScreensCount > 0;
    const screenWarningsShouldShowIcon =
        screensNotUsedCount > 0 ||
        notMatchingScreensCount > 0 ||
        hasScreenCountWarnings;
    const totalScreensToDisplay = Math.max(
        totalScreensCount,
        displayedScreensUsed + warningScreensCount
    );
    const impressionsPerScreenSummary = useMemo(() => {
        if (
            !Array.isArray(metricsImpressions) ||
            metricsImpressions.length === 0
        ) {
            return null;
        }

        return metricsImpressions.reduce(
            (totals, item) => ({
                impressions:
                    totals.impressions + (Number(item?.impressions) || 0),
                averageImpressions:
                    totals.averageImpressions +
                    (Number(item?.averageImpressions) || 0),
                expositionTime:
                    totals.expositionTime + (Number(item?.expositionTime) || 0),
            }),
            {
                impressions: 0,
                averageImpressions: 0,
                expositionTime: 0,
            }
        );
    }, [metricsImpressions]);
    const parseNumericMetric = (value) => {
        if (value === null || value === undefined) {
            return null;
        }
        if (typeof value === 'number') {
            return Number.isFinite(value) ? value : null;
        }
        const sanitizedValue = String(value).replace(/[^0-9.-]/g, '');
        if (!sanitizedValue || sanitizedValue === '-' || sanitizedValue === '.') {
            return null;
        }
        const parsedValue = Number(sanitizedValue);
        return Number.isFinite(parsedValue) ? parsedValue : null;
    };

    const formatDisplayNumber = (value) => {
        return sharedFormatDecimal(value);
    };

    const formatFrequencyNumber = (value) => {
        return sharedFormatFrequency(value);
    };

    const formatPercentageText = (value) => {
        return sharedFormatPercent(value);
    };

    const formatPercentageValue = (value) => {
        if (value === null || value === undefined || Number.isNaN(Number(value))) {
            return null;
        }
        return Number(value).toFixed(1);
    };

    const formatRatioText = (value) => {
        return sharedFormatRatio(value);
    };

    const formatCurrencyValue = (value) => {
        return sharedFormatCurrencyValue(value, currencyCode);
    };

    const generalUniverseNumeric = parseNumericMetric(
        metrics?.general_universe ?? metrics?.generalUniverse
    );
    const targetUniverseNumeric = parseNumericMetric(
        metrics?.target_universe ?? metrics?.targetUniverse
    );
    const generalImpactsNumeric = parseNumericMetric(
        metrics?.general?.total_impacts ??
            metrics?.generalMetrics?.totalImpacts ??
            metrics?.totalImpacts
    );
    const targetImpactsNumeric = parseNumericMetric(
        metrics?.target?.total_impacts ?? metrics?.targetMetrics?.totalImpacts
    );
    const generalCoverageNumeric = parseNumericMetric(
        metrics?.generalMetrics?.coverage
    );
    const targetCoverageNumeric = parseNumericMetric(
        metrics?.targetMetrics?.coverage
    );
    const generalGrpNumeric = parseNumericMetric(metrics?.generalMetrics?.grp);
    const targetGrpNumeric = parseNumericMetric(metrics?.targetMetrics?.grp);
    const generalFrequencyNumeric = parseNumericMetric(
        metrics?.generalMetrics?.frequency
    );
    const targetFrequencyNumeric = parseNumericMetric(
        metrics?.targetMetrics?.frequency
    );
    const totalImpressionsNumeric = parseNumericMetric(metrics?.totalImpressions);
    const screensQuantityNumeric = parseNumericMetric(
        metrics?.screensQuantity ?? metrics?.screens
    );
    const averageImpressionsPerScreen =
        totalImpressionsNumeric !== null &&
        screensQuantityNumeric !== null &&
        screensQuantityNumeric > 0
            ? totalImpressionsNumeric / screensQuantityNumeric
            : null;
    const averageImpressionsPerHourPerScreenInfo = useMemo(
        () =>
            resolveImpressionsPerScreenHour(
                impressionsPerScreenSummary?.averageImpressions,
                totalScreensCount
            ),
        [impressionsPerScreenSummary, totalScreensCount]
    );
    const averageImpressionsPerHourPerScreen =
        averageImpressionsPerHourPerScreenInfo.value;
    const averageImpressionsPerHourPerScreenTone =
        averageImpressionsPerHourPerScreenInfo.tone;
    const averageImpressionsPerHourPerScreenColorClass =
        averageImpressionsPerHourPerScreenTone === 'critical'
            ? 'text-red-600'
            : averageImpressionsPerHourPerScreenTone === 'warning'
              ? 'text-orange-500'
              : averageImpressionsPerHourPerScreenTone === 'ok'
                ? 'text-emerald-600'
                : 'text-gray-500';
    const averageImpressionsPerHourPerScreenColor =
        averageImpressionsPerHourPerScreenTone === 'critical'
            ? '#dc2626'
            : averageImpressionsPerHourPerScreenTone === 'warning'
              ? '#f97316'
              : averageImpressionsPerHourPerScreenTone === 'ok'
                ? '#059669'
                : '#6b7280';
    const dayDurationNumeric = parseNumericMetric(
        metrics?.dayDuration ?? metrics?.duration
    );
    const reproductionTimeNumeric = parseNumericMetric(metrics?.reproductionTime);
    const generalCpmNumeric = parseNumericMetric(metrics?.generalMetrics?.cpm);
    const targetCpmNumeric = parseNumericMetric(metrics?.targetMetrics?.cpm);
    const forecastedBudgetNumeric = parseNumericMetric(metrics?.spentBudget);
    const totalBudgetNumeric = parseNumericMetric(
        metrics?.totalBudget ?? metrics?.total_budget ?? rawBudget
    );

    const targetUniverseShare =
        generalUniverseNumeric !== null &&
        generalUniverseNumeric > 0 &&
        targetUniverseNumeric !== null
            ? (targetUniverseNumeric / generalUniverseNumeric) * 100
            : null;
    const targetImpactsShare =
        generalImpactsNumeric !== null &&
        generalImpactsNumeric > 0 &&
        targetImpactsNumeric !== null
            ? (targetImpactsNumeric / generalImpactsNumeric) * 100
            : null;
    const generalOtsOverGeneralUniverseShare =
        generalImpactsNumeric !== null &&
        generalUniverseNumeric !== null &&
        generalUniverseNumeric > 0
            ? (generalImpactsNumeric / generalUniverseNumeric) * 100
            : null;
    const targetCoverageShare =
        generalCoverageNumeric && targetCoverageNumeric !== null
            ? (targetCoverageNumeric / generalCoverageNumeric) * 100
            : null;
    const targetGrpShare =
        generalGrpNumeric && targetGrpNumeric !== null
            ? (targetGrpNumeric / generalGrpNumeric) * 100
            : null;
    const targetFrequencyShare =
        generalFrequencyNumeric !== null &&
        generalFrequencyNumeric > 0 &&
        targetFrequencyNumeric !== null
            ? (targetFrequencyNumeric / generalFrequencyNumeric) * 100
            : null;

    const concentrationIndex =
        targetUniverseShare !== null &&
        targetUniverseShare > 0 &&
        generalOtsOverGeneralUniverseShare !== null
            ? generalOtsOverGeneralUniverseShare / targetUniverseShare
            : null;

    const forecastedBudgetShare =
        totalBudgetNumeric !== null &&
        totalBudgetNumeric > 0 &&
        forecastedBudgetNumeric !== null
            ? (forecastedBudgetNumeric / totalBudgetNumeric) * 100
            : null;
    const budgetDifferencePercent =
        forecastedBudgetShare !== null
            ? Math.abs(100 - forecastedBudgetShare)
            : null;
    let budgetStatusTone = 'critical';
    if (budgetDifferencePercent === null) {
        budgetStatusTone = 'neutral';
    } else if (budgetDifferencePercent <= 2) {
        budgetStatusTone = 'optimal';
    } else if (budgetDifferencePercent <= 10) {
        budgetStatusTone = 'inefficient';
    }
    const budgetStatusLabel =
        budgetStatusTone === 'optimal'
            ? t(
                  'sections.campaigns.new-campaign.wizard-audiences.forecast.budget-status-optimal'
              )
            : budgetStatusTone === 'inefficient'
              ? t(
                    'sections.campaigns.new-campaign.wizard-audiences.forecast.budget-status-inefficient'
                )
              : budgetStatusTone === 'critical'
                ? t(
                      'sections.campaigns.new-campaign.wizard-audiences.forecast.budget-status-critical'
                  )
                : t(
                      'sections.campaigns.new-campaign.wizard-audiences.forecast.efficiency-summary-no-data'
                  );
    const budgetStatusColorClass =
        budgetStatusTone === 'optimal'
            ? 'text-emerald-600'
            : budgetStatusTone === 'inefficient'
              ? 'text-orange-500'
              : budgetStatusTone === 'critical'
                ? 'text-red-600'
                : 'text-gray-400';
    const budgetCardBorderClass =
        budgetStatusTone === 'optimal'
            ? 'border-emerald-300'
            : budgetStatusTone === 'inefficient'
              ? 'border-orange-300'
              : budgetStatusTone === 'critical'
                ? 'border-red-300'
                : 'border-gray-200';

    const cpmDeltaPercent =
        generalCpmNumeric && targetCpmNumeric !== null
            ? ((targetCpmNumeric - generalCpmNumeric) / generalCpmNumeric) * 100
            : null;
    const cpmState =
        generalCpmNumeric === null || targetCpmNumeric === null
            ? 'no-data'
            : targetCpmNumeric <= generalCpmNumeric
              ? 'efficient'
              : 'expensive';
    const cpmStateText =
        cpmState === 'no-data'
            ? t(
                  'sections.campaigns.new-campaign.wizard-audiences.forecast.efficiency-summary-no-data'
              )
                        : cpmState === 'efficient'
                            ? t(
                                        'sections.campaigns.new-campaign.wizard-audiences.forecast.cpm-more-efficient-target'
                                )
                            : t(
                                        'sections.campaigns.new-campaign.wizard-audiences.forecast.cpm-more-expensive-target'
                                );

    const baseUniverseShare = targetUniverseShare;
    const resolveComparisonTone = (comparisonShare) => {
        if (
            baseUniverseShare === null ||
            comparisonShare === null ||
            Number.isNaN(Number(comparisonShare))
        ) {
            return 'neutral';
        }
        return Number(comparisonShare) > Number(baseUniverseShare)
            ? 'efficient'
            : 'low';
    };

    const hasUnifiedReportData =
        Number(metrics.targetUniverse) > 0 &&
        metrics.totalImpacts !== 0 &&
        metrics.totalImpacts !== '-';

    const displayAllImpressionsPerScreen = () => {
        if (
            !Array.isArray(metricsImpressions) ||
            metricsImpressions.length === 0 ||
            !impressionsPerScreenSummary
        ) {
            return null;
        }

        return (
            <div className="mt-10">
                <div className="mb-2 text-md font-bold text-gray-700">
                    {t(
                        'sections.campaigns.new-campaign.wizard-audiences.forecast.hours-with-no-purchases'
                    )}
                    : {metrics?.hoursWithNoPurchases ?? 0}
                </div>
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="flex items-start justify-between px-4 py-4">
                        <div>
                            <h2 className="text-base font-semibold text-gray-900">
                                {t(
                                    'sections.campaigns.new-campaign.wizard-audiences.forecast.total-summary',
                                    'Total summary'
                                )}
                            </h2>
                            <div className="mt-3 space-y-1 text-sm font-semibold text-gray-900">
                                <div>
                                    {t(
                                        'sections.campaigns.new-campaign.wizard-audiences.forecast.total-impressions'
                                    )}
                                    :{' '}
                                    {formatMetricValue(
                                        impressionsPerScreenSummary.impressions
                                    )}
                                </div>
                                <div>
                                    {t(
                                        'sections.campaigns.new-campaign.wizard-audiences.forecast.average-impressions-per-hour'
                                    )}
                                    :{' '}
                                    {formatMetricValue(
                                        impressionsPerScreenSummary.averageImpressions
                                    )}
                                </div>
                                <div>
                                    {t(
                                        'sections.campaigns.new-campaign.wizard-audiences.forecast.total-exposure-time'
                                    )}
                                    :{' '}
                                    {formatExpositionTime(
                                        impressionsPerScreenSummary.expositionTime
                                    )}
                                </div>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() =>
                                setImpressionsPerScreenExpanded((prev) => !prev)
                            }
                            aria-expanded={impressionsPerScreenExpanded}
                            aria-controls="impressions-per-screen-details"
                            className="ml-4 inline-flex items-center rounded border border-gray-200 px-3 py-1.5 text-sm font-medium text-blue-500 transition-colors hover:text-blue-700 focus:outline-none">
                            <span className="mr-2">
                                {impressionsPerScreenExpanded
                                    ? t('common.hide-details', 'Hide details')
                                    : t('common.details', 'Details')}
                            </span>
                            <Icon
                                name={
                                    impressionsPerScreenExpanded
                                        ? 'angle-up'
                                        : 'angle-down'
                                }
                            />
                        </button>
                    </div>

                    <div
                        id="impressions-per-screen-details"
                        className={`overflow-hidden transition-all duration-300 ease-in-out ${
                            impressionsPerScreenExpanded
                                ? 'max-h-screen opacity-100 border-t'
                                : 'max-h-0 opacity-0'
                        }`}>
                        {impressionsPerScreenExpanded && (
                            <ul className="max-h-80 overflow-y-auto divide-y">
                                {metricsImpressions.map((m) => (
                                    <li
                                        key={m.screenId || m.id}
                                        className="grid grid-cols-2 md:grid-cols-3 items-center px-4 py-2">
                                        <div className="flex flex-col">
                                            {(m.screenId || m.id) && (
                                                <span className="text-sm text-gray-900">
                                                    {m.screenId || m.id}
                                                </span>
                                            )}
                                        </div>

                                        <div className="text-right col-span-1 md:col-span-2">
                                            <span className="text-sm font-semibold text-gray-900 block">
                                                {t(
                                                    'sections.campaigns.new-campaign.wizard-audiences.forecast.impressions-per-screen-title'
                                                )}
                                                : {formatMetricValue(m.impressions)}
                                            </span>
                                            <div className="text-sm font-semibold text-gray-900 block mt-1">
                                                <div>
                                                    {t(
                                                        'sections.campaigns.new-campaign.wizard-audiences.forecast.impressions-per-screen-average-impressions'
                                                    )}
                                                    :{' '}
                                                    {formatMetricValue(
                                                        m.averageImpressions
                                                    )}
                                                </div>
                                                <div className="mt-1">
                                                    {t(
                                                        'sections.campaigns.new-campaign.wizard-audiences.forecast.impressions-per-screen-exposition-time'
                                                    )}
                                                    :{' '}
                                                    {formatExpositionTime(
                                                        m.expositionTime
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            {hasUnifiedReportData ? (
                <>
                    <h1 className="mb-3">
                        {t(
                            'sections.campaigns.new-campaign.wizard-audiences.forecast.unified-report'
                        )}
                    </h1>
                    {shouldShowBudgetWarning && (
                        <div className="mb-4 flex items-start rounded-lg border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
                            <Icon
                                name="exclamation-triangle"
                                className="mr-2 mt-0.5 text-yellow-500"
                            />
                            <span>
                                {t(
                                    'common.tooltips.audiences.isUnderspend-explain'
                                )}
                            </span>
                        </div>
                    )}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div className="h-full rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-center gap-2 text-gray-700">
                                    <Icon
                                        name="users"
                                        className="text-blue-400"
                                    />
                                    <span className="font-medium">
                                        {t(
                                            'sections.campaigns.new-campaign.wizard-audiences.forecast.total-universe'
                                        )}
                                    </span>
                                </div>
                                <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700">
                                    {t(
                                        'sections.campaigns.new-campaign.wizard-audiences.forecast.target-share-pill',
                                        {
                                            share: formatPercentageText(
                                                targetUniverseShare
                                            ),
                                        }
                                    )}
                                </span>
                            </div>
                            <div className="mt-4 grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs uppercase tracking-wide text-blue-500">
                                        {t(
                                            'sections.campaigns.new-campaign.wizard-audiences.forecast.target-label'
                                        )}
                                    </p>
                                    <p className="mt-1 text-3xl font-semibold text-blue-600">
                                        {formatDisplayNumber(
                                            metrics?.targetUniverse
                                        )}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs uppercase tracking-wide text-gray-400">
                                        {t(
                                            'sections.campaigns.new-campaign.wizard-audiences.forecast.general-label'
                                        )}
                                    </p>
                                    <p className="mt-1 text-3xl font-semibold text-gray-800">
                                        {formatDisplayNumber(
                                            metrics?.generalUniverse
                                        )}
                                    </p>
                                </div>
                            </div>
                            <div className="mt-4 h-2 overflow-hidden rounded bg-gray-100">
                                <div
                                    className="h-full rounded bg-blue-500"
                                    style={{
                                        width: `${Math.max(
                                            0,
                                            Math.min(
                                                Number(
                                                    targetUniverseShare ?? 0
                                                ),
                                                100
                                            )
                                        )}%`,
                                    }}
                                />
                            </div>
                        </div>

                        <div className="h-full rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                            <div className="flex items-center gap-2 text-gray-700">
                                <Icon
                                    name="eye"
                                    className="text-gray-500"
                                />
                                <span className="font-medium">
                                    {t(
                                        'sections.campaigns.new-campaign.wizard-audiences.forecast.ad-impressions-card'
                                    )}
                                </span>
                            </div>
                            <p className="mt-4 text-3xl font-semibold text-gray-800">
                                {formatDisplayNumber(
                                    metrics?.adImpressions ??
                                        metrics?.totalImpacts
                                )}
                            </p>
                            <div className="mt-3 flex items-baseline justify-between gap-3 text-sm">
                                <span className="text-gray-500">
                                    {t(
                                        'sections.campaigns.new-campaign.wizard-audiences.forecast.impressions-per-screen-average',
                                        {
                                            value: formatSingleDecimal(
                                                averageImpressionsPerScreen
                                            ),
                                        }
                                    )}
                                </span>
                                <span
                                    className={`font-semibold text-right ${averageImpressionsPerHourPerScreenColorClass}`}
                                    style={{ color: averageImpressionsPerHourPerScreenColor }}>
                                    {averageImpressionsPerHourPerScreen !== null
                                        ? averageImpressionsPerHourPerScreen <= 0
                                            ? t(
                                                  'sections.campaigns.new-campaign.wizard-audiences.forecast.impressions-per-screen-hour-below-one'
                                              )
                                            : t(
                                                  'sections.campaigns.new-campaign.wizard-audiences.forecast.impressions-per-screen-hour-value',
                                                  {
                                                      value: averageImpressionsPerHourPerScreen,
                                                  }
                                              )
                                        : '-'}
                                </span>
                            </div>
                        </div>

                        <div className="h-full rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                            <div className="flex items-center gap-2 text-gray-700">
                                <Icon
                                    name="clock"
                                    className="text-gray-500"
                                />
                                <span className="font-medium">
                                    {t(
                                        'sections.campaigns.new-campaign.wizard-audiences.forecast.duration'
                                    )}
                                </span>
                            </div>
                            <p className="mt-4 text-3xl font-semibold text-gray-800">
                                {formatWholeNumber(dayDurationNumeric)}{' '}
                                <span className="text-lg font-medium text-gray-500">
                                    {t(
                                        'sections.campaigns.new-campaign.wizard-audiences.forecast.duration-unit-days'
                                    )}
                                </span>
                            </p>
                            <p className="mt-3 text-sm text-gray-500">
                                {t(
                                    'sections.campaigns.new-campaign.wizard-audiences.forecast.exposition',
                                    {
                                        value: formatExpositionTime(
                                            reproductionTimeNumeric
                                        ),
                                    }
                                )}
                            </p>
                        </div>

                        {[
                            {
                                key: 'impacts',
                                icon: 'eye',
                                title: t(
                                    'sections.campaigns.new-campaign.wizard-audiences.forecast.impacts'
                                ),
                                generalValue: formatDisplayNumber(
                                    metrics?.generalMetrics?.totalImpacts
                                ),
                                targetValue: formatDisplayNumber(
                                    metrics?.targetMetrics?.totalImpacts
                                ),
                                share: targetImpactsShare,
                            },
                            {
                                key: 'coverage',
                                icon: 'bullseye-arrow',
                                title: t(
                                    'sections.campaigns.new-campaign.wizard-audiences.forecast.coverage'
                                ),
                                generalValue: formatCoveragePercent(
                                    metrics?.generalMetrics?.coverage
                                ),
                                targetValue: formatCoveragePercent(
                                    metrics?.targetMetrics?.coverage
                                ),
                                share: targetCoverageShare,
                            },
                            {
                                key: 'grp',
                                icon: 'analytics',
                                title: t(
                                    'sections.campaigns.new-campaign.wizard-audiences.forecast.GRP'
                                ),
                                generalValue: formatDisplayNumber(
                                    metrics?.generalMetrics?.grp
                                ),
                                targetValue: formatDisplayNumber(
                                    metrics?.targetMetrics?.grp
                                ),
                                share: targetGrpShare,
                            },
                            {
                                key: 'frequency',
                                icon: 'repeat',
                                title: t(
                                    'sections.campaigns.new-campaign.wizard-audiences.forecast.frequency'
                                ),
                                generalValue: formatFrequencyNumber(
                                    metrics?.generalMetrics?.frequency
                                ),
                                targetValue: formatFrequencyNumber(
                                    metrics?.targetMetrics?.frequency
                                ),
                                share: targetFrequencyShare,
                            },
                        ].map((metricItem) => {
                            const tone = resolveComparisonTone(metricItem.share);
                            return (
                                <div
                                    key={metricItem.key}
                                    className="h-full rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                                    <div className="flex items-center gap-2 text-gray-700">
                                        <Icon
                                            name={metricItem.icon}
                                            className="text-gray-500"
                                        />
                                        <span className="font-medium">
                                            {metricItem.title}
                                        </span>
                                    </div>
                                    <div className="mt-3 grid grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-xs uppercase tracking-wide text-blue-500">
                                                {t(
                                                    'sections.campaigns.new-campaign.wizard-audiences.forecast.target-label'
                                                )}
                                            </p>
                                            <p className="mt-1 text-3xl font-semibold text-blue-600">
                                                {metricItem.targetValue}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-xs uppercase tracking-wide text-gray-400">
                                                {t(
                                                    'sections.campaigns.new-campaign.wizard-audiences.forecast.general-label'
                                                )}
                                            </p>
                                            <p className="mt-1 text-3xl font-semibold text-gray-800">
                                                {metricItem.generalValue}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="mt-4 h-2 overflow-hidden rounded bg-gray-100">
                                        <div
                                            className="h-full rounded bg-blue-500"
                                            style={{
                                                width: `${Math.max(
                                                    0,
                                                    Math.min(
                                                        Number(
                                                            metricItem.share ??
                                                                0
                                                        ),
                                                        100
                                                    )
                                                )}%`,
                                            }}
                                        />
                                    </div>
                                    <div className="mt-2 flex items-center justify-between text-sm">
                                        <span className="text-gray-500">
                                            {formatPercentageText(
                                                metricItem.share
                                            )}{' '}
                                            {t(
                                                'sections.campaigns.new-campaign.wizard-audiences.forecast.of-general'
                                            )}
                                        </span>
                                        <span
                                            className={`font-medium ${
                                                tone === 'efficient'
                                                    ? 'text-emerald-600'
                                                    : tone === 'low'
                                                      ? 'text-amber-600'
                                                      : 'text-gray-400'
                                            }`}>
                                            {tone === 'efficient'
                                                ? t(
                                                      'sections.campaigns.new-campaign.wizard-audiences.forecast.status-efficient'
                                                  )
                                                : tone === 'low'
                                                  ? t(
                                                        'sections.campaigns.new-campaign.wizard-audiences.forecast.status-low'
                                                    )
                                                  : t(
                                                        'sections.campaigns.new-campaign.wizard-audiences.forecast.efficiency-summary-no-data'
                                                    )}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}

                        <div className="h-full rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                            <div className="flex items-center gap-2 text-gray-700">
                                <Icon
                                    name="wallet"
                                    className="text-gray-500"
                                />
                                <span className="font-medium">
                                    {t(
                                        'sections.campaigns.new-campaign.wizard-audiences.forecast.budget-card-title'
                                    )}
                                </span>
                            </div>
                            <div className="mt-3 grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs uppercase tracking-wide text-blue-500">
                                        {t(
                                            'sections.campaigns.new-campaign.wizard-audiences.forecast.forecasted-label'
                                        )}
                                    </p>
                                    <p className="mt-1 text-3xl font-semibold text-blue-600">
                                        {formatCurrencyValue(
                                            forecastedBudgetNumeric
                                        )}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs uppercase tracking-wide text-gray-400">
                                        {t(
                                            'sections.campaigns.new-campaign.wizard-audiences.forecast.total-label'
                                        )}
                                    </p>
                                    <p className="mt-1 text-3xl font-semibold text-gray-800">
                                        {formatCurrencyValue(totalBudgetNumeric)}
                                    </p>
                                </div>
                            </div>
                            <div className="mt-4 h-2 overflow-hidden rounded bg-gray-100">
                                <div
                                    className="h-full rounded bg-blue-500"
                                    style={{
                                        width: `${Math.max(
                                            0,
                                            Math.min(
                                                Number(
                                                    forecastedBudgetShare ?? 0
                                                ),
                                                100
                                            )
                                        )}%`,
                                    }}
                                />
                            </div>
                            <div className="mt-2 flex items-center justify-between text-sm">
                                <span className="text-gray-500">
                                    {formatPercentageText(forecastedBudgetShare)}{' '}
                                    {t(
                                        'sections.campaigns.new-campaign.wizard-audiences.forecast.of-total'
                                    )}
                                </span>
                                <span
                                    className={`font-medium ${budgetStatusColorClass}`}>
                                    {budgetStatusLabel}
                                </span>
                            </div>
                        </div>

                        <div className="h-full rounded-2xl border border-yellow-200 bg-yellow-50 p-4 shadow-sm">
                            <div className="flex h-full flex-col justify-between gap-4">
                                <div>
                                    <div className="flex items-center gap-2 text-yellow-800">
                                        <Icon
                                            name="coin"
                                            className="text-yellow-700"
                                        />
                                        <p className="font-medium">
                                            {t(
                                                'sections.campaigns.new-campaign.wizard-audiences.forecast.cpm-card-title'
                                            )}
                                        </p>
                                    </div>
                                    <div className="mt-2 grid grid-cols-2 gap-6">
                                        <div>
                                            <p className="text-xs uppercase tracking-wide text-yellow-700">
                                                {t(
                                                    'sections.campaigns.new-campaign.wizard-audiences.forecast.target-label'
                                                )}
                                            </p>
                                            <p className="mt-1 text-3xl font-semibold text-yellow-700">
                                                {formatCurrencyValue(
                                                    metrics?.targetMetrics?.cpm
                                                )}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-xs uppercase tracking-wide text-yellow-700">
                                                {t(
                                                    'sections.campaigns.new-campaign.wizard-audiences.forecast.general-label'
                                                )}
                                            </p>
                                            <p className="mt-1 text-3xl font-semibold text-gray-800">
                                                {formatCurrencyValue(
                                                    metrics?.generalMetrics?.cpm
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p
                                        className={`text-3xl font-semibold ${
                                            cpmState === 'efficient'
                                                ? 'text-emerald-600'
                                                : cpmState === 'expensive'
                                                  ? 'text-yellow-800'
                                                  : 'text-gray-400'
                                        }`}>
                                        {cpmState === 'no-data'
                                            ? '-'
                                            : formatPercentageText(
                                                  Math.abs(cpmDeltaPercent)
                                              )}
                                    </p>
                                    <p
                                        className={`mt-1 text-base ${
                                            cpmState === 'efficient'
                                                ? 'text-emerald-600'
                                                : cpmState === 'expensive'
                                                  ? 'text-yellow-800'
                                                  : 'text-gray-400'
                                        }`}>
                                        {cpmStateText}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="h-full rounded-2xl border border-gray-200 bg-white p-4 shadow-sm md:col-span-2">
                            <div className="flex items-center justify-between gap-4">
                                <div className="flex items-center gap-2 text-gray-700">
                                    <Icon
                                        name="sign"
                                        className="text-gray-500"
                                    />
                                    <span className="text-2xl font-medium">
                                        {t(
                                            'sections.campaigns.new-campaign.wizard-audiences.forecast.screens'
                                        )}
                                    </span>
                                </div>
                                <p className="text-3xl font-semibold text-gray-800">
                                    {displayedScreensUsed}
                                    <span className="text-2xl font-light text-gray-400">
                                        {' / '}
                                        {totalScreensToDisplay}
                                    </span>
                                </p>
                            </div>

                            {hasScreenCountWarnings ? (
                                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3">
                                    <p className="text-sm font-medium text-amber-800">
                                        {t(
                                            'sections.campaigns.new-campaign.wizard-audiences.forecast.screens-without-purchases-title',
                                            {
                                                count:
                                                    screenCountWarnings.length,
                                            }
                                        )}
                                    </p>
                                    <ul className="mt-2 space-y-1">
                                        {screenCountWarnings.map((screen) => (
                                            <li
                                                key={screen.screen_id}
                                                className="text-sm text-amber-700">
                                                {screen.screen_external_id}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ) : (
                                <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                                    <p className="text-sm font-medium text-emerald-700">
                                        {t(
                                            'sections.campaigns.new-campaign.wizard-audiences.forecast.screens-available-message'
                                        )}
                                    </p>
                                </div>
                            )}

                            {screenWarningsShouldShowIcon &&
                                hasScreenCountWarnings && (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setNonBuyingScreensExpanded(
                                                (prev) => !prev
                                            )
                                        }
                                        aria-expanded={nonBuyingScreensExpanded}
                                        aria-controls="non-buying-screens-details"
                                        aria-label={
                                            nonBuyingScreensExpanded
                                                ? t(
                                                      'common.hide-details',
                                                      'Hide details'
                                                  )
                                                : t('common.details', 'Details')
                                        }
                                        className="mt-3 inline-flex items-center rounded bg-blue-50 px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-100">
                                        {nonBuyingScreensExpanded
                                            ? t(
                                                  'sections.campaigns.new-campaign.wizard-audiences.forecast.screens-without-purchases-title',
                                                  {
                                                      count:
                                                          screenCountWarnings.length,
                                                  }
                                              )
                                            : t('common.details', 'Details')}
                                    </button>
                                )}

                            {renderNonBuyingScreensSummary()}
                        </div>

                        <div className="rounded-2xl bg-blue-600 p-5 text-white shadow-sm md:col-span-2">
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_auto] md:items-center">
                                <div>
                                    <p className="text-xl font-medium text-blue-100">
                                        {t(
                                            'sections.campaigns.new-campaign.wizard-audiences.forecast.efficiency-summary-title'
                                        )}
                                    </p>
                                    {concentrationIndex === null ||
                                    formatPercentageValue(
                                        targetUniverseShare
                                    ) === null ||
                                    formatPercentageValue(
                                        generalOtsOverGeneralUniverseShare
                                    ) ===
                                        null ? (
                                        <p className="mt-2 text-3xl font-semibold text-white">
                                            {t(
                                                'sections.campaigns.new-campaign.wizard-audiences.forecast.efficiency-summary-no-data'
                                            )}
                                        </p>
                                    ) : (
                                        <p className="mt-2 text-4xl leading-tight font-semibold text-white">
                                            {t(
                                                'sections.campaigns.new-campaign.wizard-audiences.forecast.efficiency-summary-copy',
                                                {
                                                    targetUniverseShare:
                                                        formatPercentageText(
                                                            targetUniverseShare
                                                        ),
                                                    generalOtsOverUniverseShare:
                                                        formatPercentageText(
                                                            generalOtsOverGeneralUniverseShare
                                                        ),
                                                }
                                            )}
                                        </p>
                                    )}
                                </div>
                                <div className="text-right">
                                    <p className="text-6xl font-semibold text-white">
                                        {formatRatioText(concentrationIndex)}
                                    </p>
                                    <p className="text-lg text-blue-100">
                                        {t(
                                            'sections.campaigns.new-campaign.wizard-audiences.forecast.efficiency-summary-index'
                                        )}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                <></>
            )}
            <ScreenDiagnostics
                setSelectedScreensLocal={setSelectedScreensLocal}
                selectedScreens={campaign?.selectedScreens || []}
                selectedScreensLocal={selectedScreensLocal}
                onClearScreensWithoutProvider={onClearScreensWithoutProvider}
                onUpdateSelectedScreens={onUpdateSelectedScreens}
            />
            {displayAllImpressionsPerScreen()}
            <TableComp
                canViewForecastDetails={canViewDetails}
                selectedScreensLocal={selectedScreensLocal}
                tierList={tierList}
                currencySymbol={currencySymbol}
                campaign={campaign}
                forecastPeriod={
                    basics?.timing?.periodValue ?? campaign?.timing?.periodValue
                }
                calculatingForecast={calculatingForecast}
            />
        </>
    );
};

export default ForecastMetrics;
